<template>
    <b-row>
        <b-col>
            <card title="Patient Allergy and Meds Updates">
                <b-form-checkbox v-model="includeLocked" @change="getUnprocessedPatientAllergyUpdates" class="mb-3">
                    Show locked items
                </b-form-checkbox>

                <div v-for="(allergy, index) in filteredAllergyUpdates" :key="allergy.id">
                    <card style="cursor:pointer" @click.native="toggleInfo(allergy)">
                        <b>{{ allergy.patient.lastName + ', ' + allergy.patient.firstName }}</b> - Submitted {{
                            getAge(allergy.submitted) }}
                        <b-badge v-if="isLockedByOther(allergy)" variant="warning" class="ml-2">
                            Locked by {{ allergy.lockedBy }}
                        </b-badge>
                    </card>
                    <b-collapse :id="'panel-' + allergy.id.toString()" visible.native="0">
                        <card>
                            <b-row class="mt-2" v-if="shouldLoad(allergy)">
                                <b-col cols="5">
                                    <card plain>
                                        <b>Patient Submitted Update</b><br /><br />
                                        <span style="white-space: pre-wrap;">{{ allergy.allergyUpdate }}</span>
                                        <hr />
                                        <div class="d-flex">
                                            <div class="flex-fill">
                                                <b-button variant="success" class="px-5"
                                                    :disabled="!isLockHeldByMe(allergy)"
                                                    @click="processAllergy(patientsAllergies[calculatedIndex(index)], allergy.id)">
                                                    <strong>Processed</strong>
                                                </b-button>
                                                <span v-if="isLockedByOther(allergy)" class="ml-3 text-warning">
                                                    View only - locked by {{ allergy.lockedBy }}
                                                </span>
                                            </div>
                                            <div>
                                                <b-button
                                                    :to="{ name: 'PatientDashboard', params: { id: allergy.patient.id } }"
                                                    target="_blank" variant="info">
                                                    <i class="fa fa-user" />
                                                    Go to Profile
                                                </b-button>
                                            </div>
                                        </div>
                                    </card>
                                    <card plain>
                                        <PatientDemographicsComponent :value="allergy.patient" />
                                    </card>
                                </b-col>
                                <b-col cols="7">
                                    <card plain>
                                        <b>Patient Allergy Management</b>
                                        <PatientAllergyComponent v-model="patientsAllergies[calculatedIndex(index)]"
                                            :patient="allergy.patient" />
                                    </card>
                                    <card plain>
                                        <b>Patient Diagnosis Management</b>
                                        <GSDDDiagnosisComponent :patient="allergy.patient"
                                            v-model="patientDiagnoses[calculatedIndex(index)]"
                                            :disable="!canManageDiagnoses" />
                                    </card>
                                    <card plain>
                                        <b>Patient Other Medications Management</b>
                                        <OtherMedicationsComponent :patient="allergy.patient"
                                            :disabled="!canManageNonProfileMeds" />
                                    </card>
                                </b-col>
                            </b-row>
                        </card>
                    </b-collapse>
                </div>
                <BottomPagerBar v-model="pageNumber" :total-rows="unprocessedPatientAllergyUpdates.length"
                    :per-page="pageSize" :loading="loading" show-page-size-selector
                    @change-page-size="pageSize = $event" />
            </card>
        </b-col>
    </b-row>
</template>

<script lang="ts">
import { Component, Mixins, Watch } from 'vue-property-decorator';
import Axios from 'axios';
import moment from 'moment';
import SearchComponent from '@/components/SearchComponent.vue';
import PatientAllergyComponent from '@/components/GSDDAllergies/GSDDAllergyComponent.vue';
import { GSDDPatientAllergy, GSDDPatientDiagnosis } from "@/models/GSDD";
import PatientDemographicsComponent from '@/components/Patient/PatientDemographicsComponent.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import GSDDDiagnosisComponent from "@/components/GSDDDiagnosis/GSDDDiagnosisComponent.vue";
import { PatientAllergyUpdate } from "@/models/PatientAllergyUpdate";
import BottomPagerBar from "@/components/BottomPagerBar.vue";
import OtherMedicationsComponent from '@/components/Patient/OtherMedicationsComponent.vue';
import AutoModeHotkeyMixin from '@/mixins/AutoModeHotkeyMixin';
import ClinicalPermissionsMixin from '@/mixins/ClinicalPermissionsMixin';
import { LockHandler } from '@/mixins/LockHandler';
import { EntityType } from '@/models/EntityType';
import { getPageSizePreference, savePageSizePreference } from '@/util/pageSizePreference';
import { isLockedByOtherUser } from '@/util/LockUtils';

const LOCK_REFRESH_MS = 60000;

@Component({
    name: "PatientWebAllergyUpdateQueue",
    components: {
        BottomPagerBar,
        GSDDDiagnosisComponent,
        SearchComponent,
        PatientAllergyComponent,
        PatientDemographicsComponent,
        OtherMedicationsComponent
    },
})

export default class PatientWebAllergyUpdateQueue extends Mixins(AutoModeHotkeyMixin, LockHandler, ClinicalPermissionsMixin) {
    protected unprocessedPatientAllergyUpdates: PatientAllergyUpdate[] = [];
    protected pageNumber = 1;
    protected pageSize = 50;
    protected loading = false;
    protected visibleAllergies: PatientAllergyUpdate[] = [];
    protected includeLocked = false;
    protected heldLockIds: number[] = [];

    protected patientsAllergies: GSDDPatientAllergy[][] = [];
    protected patientDiagnoses: GSDDPatientDiagnosis[][] = [];

    async created() {
        this.pageSize = getPageSizePreference(this, 'pageSize_AllergyUpdateQueue', 50);
        await this.getUnprocessedPatientAllergyUpdates();

        const allergyId = this.$route.params.allergyId;
        if (allergyId) {
            await this.loadAndShowItem(parseInt(allergyId));
        }
    }

    @Watch('$route')
    async onRouteChange(newRoute: any, oldRoute: any) {
        if (newRoute.name !== oldRoute.name) {
            return;
        }

        const oldAllergyId = oldRoute.params.allergyId;
        const newAllergyId = newRoute.params.allergyId;

        if (oldAllergyId !== newAllergyId && newAllergyId) {
            console.log('Route allergyId parameter changed, loading new allergy item');
            await this.getUnprocessedPatientAllergyUpdates();
            await this.loadAndShowItem(parseInt(newAllergyId));
        }
    }

    async loadAndShowItem(id: number) {
        try {
            const itemIndex = this.unprocessedPatientAllergyUpdates.findIndex(a => a.id === id);

            if (itemIndex >= 0) {
                const pageNumber = Math.floor(itemIndex / this.pageSize) + 1;
                this.pageNumber = pageNumber;

                const allergyUpdate = this.unprocessedPatientAllergyUpdates[itemIndex];

                const acquired = await this.tryAcquireLock(allergyUpdate);
                if (!acquired) return;

                if (!this.visibleAllergies.includes(allergyUpdate)) {
                    this.visibleAllergies.push(allergyUpdate);
                }

                await this.$nextTick();
                this.$root.$emit('bv::toggle::collapse', `panel-${allergyUpdate.id}`);
            } else {
                await this.loadSingleItem(id);
            }
        } catch (error) {
            console.error('Error loading and showing allergy item:', error);
            this.$notification(NotificationOptions.error('Failed to load allergy update'));
        }
    }

    async loadSingleItem(id: number) {
        try {
            const response = await Axios.get<PatientAllergyUpdate>(`/PatientAllergyUpdate/${id}`);
            const allergyUpdate = new PatientAllergyUpdate(response.data);

            if (allergyUpdate) {
                this.unprocessedPatientAllergyUpdates.unshift(allergyUpdate);
                this.pageNumber = 1;

                const acquired = await this.tryAcquireLock(allergyUpdate);
                if (!acquired) return;

                this.visibleAllergies.push(allergyUpdate);

                await this.$nextTick();
                this.$root.$emit('bv::toggle::collapse', `panel-${allergyUpdate.id}`);
            }
        } catch (error) {
            console.error('Error loading single allergy update:', error);
            this.$notification(NotificationOptions.error('Failed to load allergy update'));
        }
    }

    @Watch("pageSize")
    @Watch("sortBy")
    @Watch("sortDesc")
    resetPageNumber() {
        this.pageNumber = 1;
    }

    @Watch("pageSize")
    onPageSizePreferenceChanged() {
        savePageSizePreference(this, 'pageSize_AllergyUpdateQueue', this.pageSize);
    }

    @Watch("unprocessedPatientAllergyUpdates")
    resetAllergies() {
        this.patientsAllergies = this.unprocessedPatientAllergyUpdates.map(() => []);
        this.patientDiagnoses = this.unprocessedPatientAllergyUpdates.map(() => []);
    }

    get filteredAllergyUpdates() {
        const start = (this.pageNumber - 1) * this.pageSize;
        const end = this.pageNumber * this.pageSize;
        return this.unprocessedPatientAllergyUpdates.slice(start, end);
    }

    async getUnprocessedPatientAllergyUpdates() {
        try {
            this.loading = true;
            const url = this.includeLocked
                ? `/PatientAllergyUpdate/GetUnprocessed?includeLocked=true`
                : `/PatientAllergyUpdate/GetUnprocessed`;
            const response = await Axios.get<PatientAllergyUpdate[]>(url);
            this.unprocessedPatientAllergyUpdates = response.data.map(pa => new PatientAllergyUpdate(pa));
            // The reload replaces every row object, so open panels (tracked by object identity
            // in visibleAllergies) all collapse. Release the locks that backed them — otherwise
            // they keep renewing for records the user is no longer viewing.
            this.clearAllLocks();
            this.heldLockIds = [];
            this.visibleAllergies = [];
        } catch (error) {
            console.warn(error);
        } finally {
            this.loading = false;
        }
    }

    calculatedIndex(index: number) {
        return (this.pageSize * (this.pageNumber - 1)) + index;
    }

    shouldLoad(allergy: PatientAllergyUpdate): boolean {
        return this.visibleAllergies.includes(allergy);
    }

    lockUrl(id: number): string {
        return `/PatientAllergyUpdate/${id}/lock`;
    }

    isLockHeldByMe(allergy: PatientAllergyUpdate): boolean {
        if (allergy.id == null) return false;
        if (this.heldLockIds.indexOf(allergy.id) !== -1) return true;
        return !!allergy.lockedBy && allergy.lockedBy === this.$user?.userName;
    }

    isLockedByOther(allergy: PatientAllergyUpdate): boolean {
        if (allergy.id != null && this.heldLockIds.indexOf(allergy.id) !== -1) return false;
        return isLockedByOtherUser(allergy.lockedBy, this.$user?.userName);
    }

    async tryAcquireLock(allergy: PatientAllergyUpdate): Promise<boolean> {
        if (allergy.id == null) return false;
        const id = allergy.id;
        try {
            const lock = await this.addLockWithURL(this.lockUrl(id), LOCK_REFRESH_MS);
            if (this.heldLockIds.indexOf(id) === -1) this.heldLockIds.push(id);
            allergy.lockedBy = lock.lockedBy;
            allergy.locked = lock.locked;
            return true;
        } catch (error: any) {
            if (error?.response?.status == 418) {
                const lockData = error.response.data;
                allergy.lockedBy = lockData.lockedBy;
                allergy.locked = lockData.locked;
                allergy.lockExpires = lockData.expires;
                if (!this.visibleAllergies.includes(allergy)) {
                    this.visibleAllergies.push(allergy);
                }
                await this.$nextTick();
                this.$root.$emit('bv::toggle::collapse', `panel-${id}`);
                const recordName = allergy.patient
                    ? `${allergy.patient.lastName}, ${allergy.patient.firstName}`
                    : id.toString();
                this.showLockedMessage(EntityType.PatientWebAllergyUpdate, id.toString(), recordName, lockData);
                return false;
            }
            console.warn(error);
            return false;
        }
    }

    async toggleInfo(allergy: PatientAllergyUpdate) {
        if (allergy.id == null) return;
        const id = allergy.id;
        const isVisible = this.visibleAllergies.includes(allergy);

        if (isVisible) {
            if (this.isLockHeldByMe(allergy)) {
                this.releaseLockAtURL(this.lockUrl(id));
                const heldIdx = this.heldLockIds.indexOf(id);
                if (heldIdx !== -1) this.heldLockIds.splice(heldIdx, 1);
                allergy.lockedBy = null;
                allergy.locked = null;
                allergy.lockExpires = null;
            }
            const visibleIdx = this.visibleAllergies.indexOf(allergy);
            if (visibleIdx !== -1) this.visibleAllergies.splice(visibleIdx, 1);
            this.$root.$emit('bv::toggle::collapse', `panel-${id}`);
            return;
        }

        const acquired = await this.tryAcquireLock(allergy);
        if (acquired) {
            this.visibleAllergies.push(allergy);
            await this.$nextTick();
            this.$root.$emit('bv::toggle::collapse', `panel-${id}`);
        }
    }

    async processAllergy(allergyList: GSDDPatientAllergy[], patWebAllergyId: number) {
        try {
            if (allergyList.length) {
                await Axios.post(`/Allergy/SaveGSDDAllergies/${allergyList[0].patientId}`, {
                    allergies: allergyList,
                    removalReasons: [],
                    defaultRemovalReason: 'Removed via patient-submitted allergy update',
                });
            }
            await Axios.post(`PatientAllergyUpdate/ProcessPatientAllergy/${patWebAllergyId}`);

            this.releaseLockAtURL(this.lockUrl(patWebAllergyId));
            const heldIdx = this.heldLockIds.indexOf(patWebAllergyId);
            if (heldIdx !== -1) this.heldLockIds.splice(heldIdx, 1);
            await this.getUnprocessedPatientAllergyUpdates();

            await this.handleAutoModeCompletion();
        } catch (error: any) {
            if (error?.response?.status == 418) {
                const lockData = error.response.data;
                this.showLockedMessage(EntityType.PatientWebAllergyUpdate, patWebAllergyId.toString(), patWebAllergyId.toString(), lockData);
                await this.getUnprocessedPatientAllergyUpdates();
                return;
            }
            console.error(error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    async handleAutoModeCompletion() {
        await this.handleAutoModeNextItem();
    }

    getAge(date: Date) {
        return moment.utc(date).local().fromNow();
    }
}
</script>
